import React, { useRef, useContext, useState, useEffect } from "react";
import { EnhancedUploaderDashboard } from "../../EnhancedUploader/Dashboard";
import { PhotoManagerContext } from ".";
import ConfirmAbortUploader from "./ConfirmAbortUploader";
import DuplicateDecisionModal from "../../EnhancedUploader/DuplicatesDecisionModal";
import DoNotPhotographPanel from "./DoNotPhotographPanel";

interface PhotoUploaderProps {
  albumId: number;
  onUploadComplete?: () => void;
  closeUploader: () => void;
  hasVideoAccess: boolean;
  mobileCameraOnlyUpload: boolean;
}
export interface UppyDashboardRef {
  pause: () => void;
  resume: () => void;
  isUploadInProgress: () => boolean;
  hasFilesAdded: () => boolean;
  filesNotUploaded: () => boolean;
}

/** Click outside to close popup is not implemented for this popup
 to give single close option to user so the uploader flow is simple */
const PhotoUploader: React.FC<PhotoUploaderProps> = ({
  albumId,
  onUploadComplete,
  closeUploader,
  hasVideoAccess,
  mobileCameraOnlyUpload
}) => {
  const context = useContext(PhotoManagerContext);
  const [showAbortWarning, setShowAbortWarning] = useState(false);
  const [showingDuplicateModal, setShowingDuplicateModal] = useState(false);
  const uppyRef = useRef<UppyDashboardRef>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const handleCloseUploader = () => {
    if (
      uppyRef.current?.isUploadInProgress() ||
      (uppyRef.current?.hasFilesAdded() && uppyRef.current?.filesNotUploaded())
    ) {
      uppyRef.current?.pause();
      setShowAbortWarning(true);
    } else {
      closeUploader();
    }
  };

  const handleContinueUpload = () => {
    if (uppyRef.current?.isUploadInProgress()) {
      uppyRef.current?.resume();
    }
    setShowAbortWarning(false);
  };

  const handleAbortUpload = () => {
    setShowAbortWarning(false);
    closeUploader();
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (uppyRef.current?.isUploadInProgress()) {
        e.preventDefault();
        e.returnValue = ""; // This is required for some browsers
        return "Upload in progress. Are you sure you want to leave? Your uploads will be lost.";
      }
      return undefined;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [uppyRef.current?.isUploadInProgress()]);

  const UploaderAlbumPath = () => (
    <div className="uploader-path">
      <span> Library</span>
      <span className="grey-text">/</span>
      <span>{context?.selectedAlbum?.eventName}</span>
      <span className="grey-text">/</span>
      <span>{context?.selectedAlbum?.name}</span>
    </div>
  );

  const resolveRef = useRef<(value: boolean) => void>();

  const handleDuplicateDecision = (duplicates: string[]) => {
    return new Promise<boolean>(resolve => {
      resolveRef.current = resolve;
      setShowingDuplicateModal(true);
    });
  };

  const handleModalConfirm = () => {
    setShowingDuplicateModal(false);
    resolveRef.current?.(true);
  };

  const handleModalCancel = () => {
    setShowingDuplicateModal(false);
    resolveRef.current?.(false);
  };

  return (
    <>
      {context?.isMobile ? (
        <div className="ws-photo-popup-layout">
          <div className="ws-photo-popup-overlay">
            <div className="ws-photo-popup-details-mob">
              <div className=" ws-photo-upload-popup-title">
                <UploaderAlbumPath />
                <div>
                  Upload photos to{" "}
                  <span className="album-name">
                    {context?.selectedAlbum?.name}
                  </span>
                </div>
                {!context?.showDoNotPhotographInUploader && (
                  <div className="ws-legacy-news-cont">
                    <i className="fa fa-info-circle"></i>
                    <span>
                      <b>Looking for the old uploader?</b>
                      <span>We've improved this uploader for volume, reliability, and speed. Prefer the old one? You can still use it{" "}
                      <a
                        id={`legacy-uploader-${albumId}`}
                        href={context?.selectedAlbum?.uploadPhotosPath}
                        onClick={e => {
                          e.stopPropagation();
                        }}
                      >
                        right here.
                      </a>
                      </span>
                    </span>
                  </div>
                )}
              </div>
              <div>
                <button
                  className="ws-photo-popup-close"
                  onClick={() => handleCloseUploader()}
                >
                  &times;
                </button>
              </div>
            </div>
            {context?.showDoNotPhotographInUploader && (
              <DoNotPhotographPanel />
            )}
            <EnhancedUploaderDashboard
              albumId={albumId}
              onUploadComplete={onUploadComplete}
              hasVideoAccess={hasVideoAccess}
              ref={uppyRef}
              closeUploader={closeUploader}
              onDuplicateDecision={handleDuplicateDecision}
              mobileCameraOnlyUpload={mobileCameraOnlyUpload}
            />
          </div>
        </div>
      ) : (
        <div>
          <div
            id="filter-dialog"
            className="modal fade in geo-modal geo-modal--show uploader-modal"
            style={{ overflow: "unset" }}
            role="dialog"
          >
            <div
              className="modal-dialog filter-dialog-content uppy-uploader-popup"
              ref={popupRef}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <button
                    type="button"
                    className="close confirm-close"
                    onClick={() => handleCloseUploader()}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="geo-modal__header geo-modal--warning-bg">
                  <UploaderAlbumPath />

                  <div className="ws-photo-upload-popup-title">
                    <div>
                      Upload photos to{" "}
                      <span className="album-name">
                        {context?.selectedAlbum?.name}
                      </span>
                    </div>
                  </div>
                  {!context?.showDoNotPhotographInUploader && (
                    <div className="ws-legacy-news-cont">
                      <i className="fa fa-info-circle"></i>
                      <span>
                        <b>Looking for the old uploader?</b>
                        <span>We've improved this uploader for volume, reliability, and speed. Prefer the old one? You can still use it{" "}
                        <a
                          id={`legacy-uploader-${albumId}`}
                          href={context?.selectedAlbum?.uploadPhotosPath}
                          onClick={e => {
                            e.stopPropagation();
                          }}
                        >
                          right here.
                        </a>
                        </span>
                      </span>
                    </div>
                  )}
                </div>
                <div className="modal-body">
                  {context?.showDoNotPhotographInUploader && (
                    <DoNotPhotographPanel />
                  )}
                  <EnhancedUploaderDashboard
                    albumId={albumId}
                    onUploadComplete={onUploadComplete}
                    hasVideoAccess={hasVideoAccess}
                    ref={uppyRef}
                    closeUploader={closeUploader}
                    onDuplicateDecision={handleDuplicateDecision}
                    mobileCameraOnlyUpload={mobileCameraOnlyUpload}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade in" />
        </div>
      )}
      {showAbortWarning && (
        <ConfirmAbortUploader
          onContinueUpload={handleContinueUpload}
          onAbortUpload={handleAbortUpload}
        />
      )}
      {showingDuplicateModal && (
        <DuplicateDecisionModal
          onConfirm={handleModalConfirm}
          onCancel={handleModalCancel}
        />
      )}
    </>
  );
};

export default PhotoUploader;
